/* eslint-disable react-hooks/exhaustive-deps */
// src/context/AppContext.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  API_BASE,
  computeFingerprint,
  getUserProfile,
  fetchUploadHistory,
  loadPastUpload,
  fetchAccountUsage,
  fetchAccountLimits,
  syncSubscription,
  fetchModelInsights,            // + add
} from '../utils/api';

import { clearUserCache, getUserCache, setUserCache } from '../utils/userCache';
import Papa from 'papaparse';

// Use a single, consistent storage key for auth
const TOKEN_KEY = 'clairifi_token';
const USER_KEY  = 'clairifi_user';
const DEMO_CSV_PATH = '/demo/Clairifi%20Demo.csv'; // update if you rename the file
const HAS_UPLOADED_KEY = 'clairifi:hasUploaded';
// Normalize a stable user id (covers different payload shapes)
const resolveUserId = (u) => {
  if (!u) return null;
  return String(u.id ?? u.user_id ?? u.uid ?? u.sub ?? u.email ?? '');
};

export const AppContext = createContext();

export const AppProvider = ({ children }) => {
  // 🔐 Auth state
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || null);
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    try {
      const u = JSON.parse(raw);
      return { ...u, id: resolveUserId(u) };
    } catch {
      return null;
    }
  });

  // 📊 Data state
  const [csvData, setCsvData] = useState(null);
  const [columnTypes, setColumnTypes] = useState(null);
  const [companyName, setCompanyName] = useState(() => {
    try {
      return localStorage.getItem('clairifi:companyName') || 'Your Company';
    } catch {
      return 'Your Company';
    }
  });
  const [financialScale, setFinancialScale] = useState('billions');
  const [uploadedFilename, setUploadedFilename] = useState(null);
  // Demo / upload switches
  const [hasUploaded, setHasUploaded] = useState(() => {
    try { return localStorage.getItem(HAS_UPLOADED_KEY) === '1'; } catch { return false; }
  });
  const [isDemo, setIsDemo] = useState(false);
  // 🔮 Feature payloads
  const [forecastData, setForecastData] = useState(null);
  const [strategyData, setStrategyData] = useState(null);
  const [cfoToolsData, setCfoToolsData] = useState(null);
  const [modelInsightsData, setModelInsightsData] = useState(null);
  const [chatbotMessages, setChatbotMessages] = useState([]);

  // 🧮 Usage counters
  const [usage, setUsage] = useState({ strategy: 0, forecast: 0, model: 0, cfo: 0 });

  // 🔑 Dataset fingerprint (derived)
  const [fingerprint, setFingerprint] = useState(null);

  // ---------- Auth actions ----------

  const login = async (newToken) => {
    if (!newToken) return;
    localStorage.setItem(TOKEN_KEY, newToken);
    setToken(newToken);
    // Load the user profile + rehydrate any last dataset
    await fetchAndHydrateUser(newToken);
  };

  const logout = () => {
    if (user?.id) clearUserCache(user.id);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);

    // Clear volatile state
    setCsvData(null);
    setColumnTypes(null);
    setCompanyName('Your Company');
    setFinancialScale('billions');
    setUploadedFilename(null);
    setForecastData(null);
    setStrategyData(null);
    setCfoToolsData(null);
    setChatbotMessages([]);
    setFingerprint(null);
    try { localStorage.setItem(HAS_UPLOADED_KEY, '0'); } catch {}
    setHasUploaded(false);
    setIsDemo(true);
    // Immediately repopulate demo for UX
    bootDemoIfNeeded();
  };

  // ---------- Data loaders ----------

  const fetchAndHydrateUser = async (activeToken) => {
    try {
      // 1) Profile
      const userData = await getUserProfile(activeToken);
      const normalized = { ...userData, id: resolveUserId(userData) };
      setUser(normalized);
      localStorage.setItem(USER_KEY, JSON.stringify(normalized));
      // Authoritative plan from backend (reconciled with Stripe)
      try {
        const limits = await fetchAccountLimits(activeToken);
        if (limits && limits.plan) {
          const merged = {
            ...normalized,
            plan: limits.plan,
            trial_active: Boolean(limits.trial_active),
            uploads_used: limits.uploads_used,
            uploads_limit: limits.uploads_limit,
          };
          setUser(merged);
          localStorage.setItem(USER_KEY, JSON.stringify(merged));
        }
      } catch (e) {
        console.warn('[AppContext] fetchAccountLimits failed:', e?.message || e);
      }

    } catch (err) {
      console.warn('[AppContext] Failed to fetch user profile:', err);
      // keep session; some endpoints may still work with token alone
    }

      // 2) Rehydrate “last upload” ONLY if user has previously uploaded (avoid overriding demo)
      const allowRehydrate = (() => {
        try { return localStorage.getItem(HAS_UPLOADED_KEY) === '1'; } catch { return false; }
      })();
      if (!allowRehydrate) {
        console.info('[AppContext] Skipping rehydrate (no prior upload) — staying in demo mode');
        return;
      }
      // Avoid network/CPU contention on the Account page
      const here = (typeof window !== 'undefined') ? window.location.pathname : '';
      if (/\/account\/?$/.test(here)) {
        console.info('[AppContext] On /account — skipping last-upload rehydrate for now');
        return;
      }
      // Skip rehydrate only if we already have data in memory
      if (Array.isArray(csvData) && csvData.length > 0) {
        console.info('[AppContext] Data present — skipping last-upload rehydrate');
        return;
      }


      try {
        const res = await fetch(`${API_BASE}/account/last`, {
          headers: { Authorization: `Bearer ${activeToken}` },
        });
        const ct = res.headers.get('content-type') || '';
        if (!res.ok) {
          const t = await res.text();
          throw new Error(`GET /account/last ${res.status}: ${t.slice(0, 200)}`);
        }
        if (!(ct || '').includes('json')) {

          const t = await res.text();
          throw new Error(`GET /account/last non-JSON: ${t.slice(0, 200)}`);
        }
  
        const { last_csv } = await res.json();
        if (last_csv) {
          // ask backend to reload the cached dataset into a full payload
          const res2 = await fetch(`${API_BASE}/account/last/reload`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${activeToken}` },
          });
          const ct2 = res2.headers.get('content-type') || '';
          if (!res2.ok) {
            const t2 = await res2.text();
            throw new Error(`POST /account/last/reload ${res2.status}: ${t2.slice(0, 200)}`);
          }
          if (!ct2.includes('application/json')) {
            const t2 = await res2.text();
            throw new Error(`POST /account/last/reload non-JSON: ${t2.slice(0, 200)}`);
          }
          const payload = await res2.json();
          console.debug('[AppContext] /account/last/reload rows=',
            Array.isArray(payload?.data) ? payload.data.length : null,
            'cols=',
            (Array.isArray(payload?.data) && payload.data[0]) ? Object.keys(payload.data[0]).length : null,
            'first_row_keys=',
            payload?.first_row_keys || '(none)'
          );

          if (payload?.data && Array.isArray(payload.data)) {
            setCsvData(payload.data);
            setColumnTypes(payload.column_types || null);
            setCompanyName(payload.company || 'Unknown company');
            setUploadedFilename(payload.filename || null);
            setFinancialScale(payload.scale || payload.unit_scale || 'billions');
            // Persist flags so next boots skip demo and rehydrate immediately
            try { localStorage.setItem(HAS_UPLOADED_KEY, '1'); } catch {}
            setHasUploaded(true);
            setIsDemo(false);

          }
        }
      } catch (err) {
        // If nothing to rehydrate, this will 200 with no `last_csv` OR 404/204 on some backends
        console.warn('[AppContext] Rehydrate last CSV failed:', err);
      }
  };

const loadLatestUpload = async () => {
  // If we already have rows in memory (e.g., just finalized transactions), do not override
  if (!token || (Array.isArray(csvData) && csvData.length > 0)) return;
    try {
      const history = await fetchUploadHistory(token);
      if (!history || history.length === 0) return;

      // Prefer the newest CSV (ignore non-CSV artifacts)
      const latestCsv = history.find((x) => /\.csv$/i.test(x.filename));
      if (!latestCsv) return;

      const res = await loadPastUpload(latestCsv.filename, token);
      if (!res?.data || !Array.isArray(res.data)) return;

      setCsvData(res.data);
      setColumnTypes(res.column_types || null);
      setCompanyName(res.company || 'Unknown company');
      setUploadedFilename(latestCsv.filename);
      setFinancialScale(res.scale || res.unit_scale || 'billions');
      // Persist flags so next boots skip demo and rehydrate immediately
      try { localStorage.setItem(HAS_UPLOADED_KEY, '1'); } catch {}
      setHasUploaded(true);
      setIsDemo(false);
      console.log('[AUTO LOAD] Last upload auto-loaded:', latestCsv.filename);
    } catch (err) {
      console.warn('[AUTO LOAD] Failed to load last upload:', err);
    }
  };

  const refreshUsage = async () => {
    try {
      if (!token) return;
      const data = await fetchAccountUsage(token);
      setUsage({
        strategy: Number(data?.strategy || 0),
        forecast: Number(data?.forecast || 0),
        cfo: Number(data?.cfo || 0),
        model: Number(data?.model || 0),
      });
    } catch (e) {
      console.warn('[AppContext] Failed to load usage:', e);
    }
  };
  const refreshMembership = async () => {
    if (!token) return;
    try {
      // Force backend reconciliation and then read limits
      const limits = await syncSubscription(token);
      if (!limits) return;
      const current = user || {};
      const merged = {
        ...current,
        plan: limits.plan,
        trial_active: Boolean(limits.trial_active),
        uploads_used: limits.uploads_used,
        uploads_limit: limits.uploads_limit,
      };
      setUser(merged);
      localStorage.setItem(USER_KEY, JSON.stringify(merged));
      return limits;
    } catch (e) {
      console.warn('[AppContext] refreshMembership failed:', e?.message || e);
      return null;
    }
  };

  // ---------- Demo loader ----------
  const bootDemoIfNeeded = async () => {
    try {
      if (hasUploaded || csvData) return; // do nothing if user already uploaded or data is present
      const res = await fetch(DEMO_CSV_PATH, { cache: 'no-store' });
      if (!res.ok) return;
      const text = await res.text();
      const parsed = Papa.parse(text, { header: true, dynamicTyping: true, skipEmptyLines: true });
      const rows = (parsed?.data || []).filter(Boolean);
      if (rows.length) {
        setCsvData(rows);
        // Provide a minimal demo columnTypes so dashboard can compute burn/runway
        setColumnTypes({
          revenue: 'Total Revenue',
          expenses: 'Total Expenses',
          profit: 'Net Income',
          period: { year: 'Year', quarter: 'Quarter' }
        });
        setCompanyName('Demo Company');
        setUploadedFilename('Clairifi Demo.csv');
        setFinancialScale('billions');
        setIsDemo(true);
      }
    } catch (e) {
      console.warn('[AppContext] Demo CSV failed to load:', e);
    }
  };

  // ---------- Effects ----------
  // Boot demo on first load if there is no real upload
  useEffect(() => {
    bootDemoIfNeeded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUploaded]);

  // When token appears/changes, fetch user + try to rehydrate
  useEffect(() => {
    if (!token) return;
    (async () => {
      await fetchAndHydrateUser(token);
      // Avoid kicking off heavy dataset reload while user is on /account
      const here = (typeof window !== 'undefined') ? window.location.pathname : '';
      if (!/\/account\/?$/.test(here)) {
        loadLatestUpload();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);


  // Compute a stable fingerprint to index per-dataset caches
  useEffect(() => {
    if (!csvData || !columnTypes) return;
    const rowsForFp = Array.isArray(csvData) ? csvData : String(csvData);
    const fp = computeFingerprint({
      rows: rowsForFp,
      columnTypes: columnTypes || {},
      companyName: companyName || '',
    });
    setFingerprint(fp);
  }, [csvData, columnTypes, companyName]);
  // Preload Model Insights once per dataset so the page opens instantly
  useEffect(() => {
    if (!csvData) return; // need data (demo or upload)
    // Respect trial gating: allow demo, block real uploads
    if (user?.trial_active && !user?.is_admin && !isDemo) return;


    const uid = user?.id || null;
    const key = `preload:modelInsights:${uid || 'anon'}:${fingerprint || 'none'}`;
    if (!window.__preloaded) window.__preloaded = new Set();
    if (window.__preloaded.has(key)) return; // already preloaded for this dataset

    window.__preloaded.add(key);
    (async () => {
      try {
        const data = await fetchModelInsights({
          force: false,
          token,
          userId: uid,
          fingerprint,
        });
        setModelInsightsData(data); // keep in context so first render is instant
      } catch (e) {
        console.warn('[AppContext] Preload model insights skipped:', e?.message || e);
      }
    })();
  }, [csvData, fingerprint, token, user?.id, isDemo]);


  // Persist strategy/forecast payloads keyed by user+dataset
  useEffect(() => {
    if (!user?.id || !fingerprint || !strategyData) return;
    setUserCache(user.id, `strategy:${fingerprint}`, strategyData);
  }, [user?.id, fingerprint, strategyData]);
  useEffect(() => {
    if (!user?.id || !fingerprint || !modelInsightsData) return;
    setUserCache(user.id, `model:${fingerprint}`, modelInsightsData);
  }, [user?.id, fingerprint, modelInsightsData]);


  useEffect(() => {
    if (!user?.id || !forecastData) return;
    // Write using the canonical key *and* the expected wrapper shape
    setUserCache(user.id, 'forecast:last', { ts: Date.now(), payload: forecastData });
  }, [user?.id, forecastData]);

  // Rehydrate strategy when fingerprint becomes known
  useEffect(() => {
    if (!user?.id || !fingerprint) return;
    const cached = getUserCache(user.id, `strategy:${fingerprint}`);
    if (cached && !strategyData) setStrategyData(cached);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, fingerprint]);
  useEffect(() => {
    if (!user?.id || !fingerprint) return;
    const cached = getUserCache(user.id, `model:${fingerprint}`);
    if (cached && !modelInsightsData) setModelInsightsData(cached);
  }, [user?.id, fingerprint]);

  // Persist company label locally
  useEffect(() => {
    try {
      if (companyName) localStorage.setItem('clairifi:companyName', companyName);
    } catch {}
  }, [companyName]);

  // ---------- Provider value ----------
  const trialLocked = Boolean(user?.trial_active && !user?.is_admin && !isDemo);

  // Adopt a dataset payload returned by /transactions/finalize
  const adoptFinalizePayload = (payload = {}) => {
    try {
      if (payload?.data && Array.isArray(payload.data)) {
        setCsvData(payload.data);
        setColumnTypes(payload.column_types || null);
        setCompanyName(payload.company || 'Transactions (Imported)');
        setFinancialScale(payload.scale || payload.unit_scale || 'billions');
        try { localStorage.setItem(HAS_UPLOADED_KEY, '1'); } catch {}
        setHasUploaded(true);
        setIsDemo(false);
      }
    } catch (e) {
      console.warn('[AppContext] adoptFinalizePayload failed:', e?.message || e);
    }
  };
  return (
    <AppContext.Provider
      value={{
        // 🔐 Auth
        token,
        user,
        trialLocked,
        login,
        logout,
        refreshMembership,   // expose for UI/pages to force-sync after billing


        // 📊 Data
        csvData,
        setCsvData,
        columnTypes,
        setColumnTypes,
        companyName,
        setCompanyName,
        financialScale,
        setFinancialScale,
        uploadedFilename,
        setUploadedFilename,
        // 🧪 Demo / Upload switches
        hasUploaded,
        setHasUploaded,
        isDemo,
        setIsDemo,
        adoptFinalizePayload,   // ⬅ expose the new helper

        // 🔮 Feature payloads
        forecastData,
        setForecastData,
        strategyData,
        setStrategyData,
        cfoToolsData,
        setCfoToolsData,
        modelInsightsData,          // + add
        setModelInsightsData,       // + add

        // 🧠 Chat
        chatbotMessages,
        setChatbotMessages,

        // 🧮 Usage
        usage,
        refreshUsage,

        // 🔑 Fingerprint
        fingerprint,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useAppContext must be used within an AppProvider');
  return ctx;
};

